import React, {useEffect, useRef, useState} from 'react';
import {calcDiscountPrice, currencySymbol, getMediaURL, price} from "../../../../../config/values";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../../config/design/styles";
import {
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import {Star, X} from "lucide-react";
import AddToCartBtn from "./AddToCartBtn";
import SubTitle from "../../../../components/elements/SubTitle";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "axios";
import { defaultVariables } from "../../../../../config";
import Grid from "@mui/material/Grid2";
import SwiperSlider from "../../../../components/elements/slider/SwiperSlider";
import ProductDialogSkeleton from "../../../../components/loaders/ProductDialogSkeleton";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";

function getPurchaseCooldownMessage(item, t) {
    if (!item?.purchase_locked) return null;
    if (item.purchase_remaining_days) {
        return t('purchaseAvailableInDays', { days: item.purchase_remaining_days });
    }
    if (item.purchase_available_at) {
        const date = moment.utc(item.purchase_available_at).tz('Europe/Istanbul').locale('tr').format('YYYY/M/D');
        return t('purchaseAvailableFromDate', { date });
    }
    return null;
}

function SingleCardProduct({ product, classes, addtocart,disabled }) {
    const { t } = useTranslation();

    const [data, setData] = useState(product);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const navigation = useNavigate();
    const videoRefs = useRef([]);
    const loginToken = localStorage.getItem('ACT');
    const config = {
        headers: {
            Accept: 'application/json',
            ...(!!loginToken ? { Authorization: `Bearer ${loginToken}` } : {}),
        }
    }
    const { enqueueSnackbar } = useSnackbar();

    const loadData = () => {
        setLoading(true);
        axios.get(`${defaultVariables.url}/front/product/${product.id}`, config)
            .then(response => {
                setData(response.data);
                setLoading(false);
                setDataLoaded(true);
            })
            .catch(error => {
                console.error("Error fetching item data:", error);
                setLoading(false);
            });
    }

    useEffect(() => {
        if (open && !dataLoaded) {
            loadData();
        }
        if (!open) {
            videoRefs.current.forEach((video) => {
                if (video && !video.paused) video.pause();
            });
        }
    }, [open, dataLoaded]);

    useEffect(() => {
        setData(product);
    }, [product]);

    const cardCooldownMessage = getPurchaseCooldownMessage(product, t);
    const dialogCooldownMessage = getPurchaseCooldownMessage(data, t);
    const cardLocked = !!product.purchase_locked;
    const dialogLocked = !!data.purchase_locked;

    return (
        <>
            <Card className={clsx(classes.SingleCardProduct, 'clickable')} onClick={() => setOpen(true)}>
                <div className="media-holder">
                    <img loading="lazy" src={getMediaURL(product.thumbnail, 'square')} alt={product.name} />
                    {
                        !!product.discount_percent &&
                        <span className="discount_percent">{product.discount_percent}%</span>
                    }
                </div>
                <CardContent  className="card-content">
                    <SubTitle component="span" align="left" className="product-title">{product.name}</SubTitle>
                    <Typography className="product-desc">{product.description}</Typography>
                    {
                        !!product.average_rate &&
                        <span className='rating-holder'><Star className="rating-icon" /> {product.average_rate}</span>

                    }
                    {
                        !!cardCooldownMessage &&
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {cardCooldownMessage}
                        </Typography>
                    }
                    <div className='prd-card-footer'>
                        <Typography className="pricing">
                            {
                                !!product.discount_percent ?
                                    <>
                                        <del>{price(product.price)} {currencySymbol}</del>
                                        <strong>{price(calcDiscountPrice(product.price, product.discount_percent))} {currencySymbol}</strong>
                                    </>
                                    :
                                    <strong>{price(product.price)} {currencySymbol}</strong>
                            }
                        </Typography>
                        {
                            !!addtocart &&
                            <AddToCartBtn product={product} icon disabled={disabled || cardLocked} />
                        }
                    </div>

                </CardContent>
            </Card>
            <Dialog fullWidth maxWidth="sm" open={open} onClose={() => setOpen(false)} className={classes.productDialog}>
                <DialogTitle>
                    <SubTitle component="span" align="left" className="dialog-title">{data.name}</SubTitle>
                    <IconButton
                        onClick={() => setOpen(false)}
                        className={classes.modalCloseButton}
                        size="small"
                    >
                        <X fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {
                        !!loading ?
                            <ProductDialogSkeleton />
                            :
                            <Grid container spacing={2} justifyContent="center">
                                <Grid item size={{ xs: 10, md: 8, lg: 7 }}>
                                    <SwiperSlider
                                        data={!!data.images?.length ? data.images : [null]}
                                        spaceBetween={10}
                                        slidesPerView={1}
                                        showNavigation={true}
                                        showPagination={true}
                                        renderSlide={(item, index) => {
                                            const mediaUrl = getMediaURL(item);

                                            if (!item) return <div className="slider-img placeholder" />;

                                            return item.type === 'video' ? (
                                                <div className='slider-video-holder'>
                                                    <video
                                                        className="slider-img"
                                                        controls
                                                        ref={(el) => (videoRefs.current[index] = el)}
                                                    >
                                                        <source src={mediaUrl} type="video/mp4" />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                </div>
                                            ) : (
                                                <img className="slider-img" alt={item?.alt || 'Media'} src={mediaUrl} />
                                            );
                                        }}
                                    />

                                </Grid>
                                <Grid item size={12}>
                                    <Typography>{data.description}</Typography>
                                </Grid>
                                {
                                    !!dialogCooldownMessage &&
                                    <Grid item size={12}>
                                        <Typography color="error">{dialogCooldownMessage}</Typography>
                                    </Grid>
                                }
                            </Grid>
                    }

                </DialogContent>
                <DialogActions className="action-holder product-action">
                    <div><Typography className="pricing">
                        {
                            !!data.discount_percent ?
                                <>
                                    <del>{price(data.price)} {currencySymbol}</del>
                                    <strong>{price(calcDiscountPrice(data.price, data.discount_percent))} {currencySymbol}</strong>
                                </>
                                :
                                <strong>{price(data.price)} {currencySymbol}</strong>
                        }
                    </Typography></div>
                    <div>
                        <AddToCartBtn product={data} disabled={disabled || dialogLocked}/>
                    </div>
                </DialogActions>
            </Dialog>
        </>

    );
}

export default withStyles(Styles)(SingleCardProduct);
